import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../../lib/i18n';

export interface AsyncData<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  /** Yeniden yükle. `silent`: mevcut veri ekranda kalsın, yükleniyor gösterilmesin. */
  reload: (silent?: boolean) => Promise<void>;
}

/**
 * Tek bir IPC çağrısının yükleme / hata / yeniden yükleme durumu.
 * `key` değişince (ör. başka bir kişinin profili) yeniden yüklenir; eşzamanlı
 * iki yüklemeden yalnızca sonuncusu ekrana yazılır.
 */
export function useAsyncData<T>(loader: (() => Promise<T>) | null, key: unknown): AsyncData<T> {
  const [state, setState] = useState<{ data: T | null; isLoading: boolean; error: string | null }>({
    data: null,
    isLoading: Boolean(loader),
    error: null,
  });
  const requestRef = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async (silent = false) => {
    const current = loaderRef.current;
    const id = ++requestRef.current;
    if (!current) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    if (!silent) setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await current();
      if (id === requestRef.current) setState({ data, isLoading: false, error: null });
    } catch (err) {
      if (id === requestRef.current) {
        setState((prev) => ({ ...prev, isLoading: false, error: (err as Error).message || t('Yüklenemedi.', 'Could not load.') }));
      }
    }
  }, []);

  useEffect(() => {
    void reload();
    return () => {
      // Ayrılan bileşene geç gelen yanıt yazılmasın.
      requestRef.current += 1;
    };
  }, [key, reload]);

  return { ...state, reload };
}
